import React, { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Breadcrumbs,
  Button,
  Card,
  CardContent,
  Chip,
  Link,
  Sheet,
  Table,
  ThemeProvider,
  Typography,
  CssBaseline,
  Grid,
} from '@mui/joy';
import {
  KeyboardArrowRight,
  UploadFile as UploadFileIcon,
  CloudUpload as CloudUploadIcon,
  Error as ErrorIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Download as DownloadIcon,
  DoneAll as DoneAllIcon,
  PlaylistAddCheck as PlaylistAddCheckIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import { DataGrid } from '@mui/x-data-grid';
import { theme } from '../../main/theme/constants';
import NativeCheckbox from '../../main/components/NativeCheckbox';
import PermissionGate from '../../main/components/PermissionGate';
import { PERMISSIONS } from '../../main/context/AuthContext';
import { usePermissions } from '../../main/hooks/usePermissions';
import {
  getPricingRulesImportTemplate,
  getPricingRulesImportSample,
  postPricingRulesImportPreview,
  postPricingRulesImportConfirm,
} from '../../main/API/pricingRule';

const PREFERRED_PREVIEW_KEYS = [
  'code',
  'name',
  'name_ar',
  'plan_code',
  'scope',
  'strategy',
  'tariff_code',
  'category_code',
  'customer',
  'shipment_type',
  'shipment_unit_id',
  'destination_city',
  'unit_price',
  'total_price',
  'percent',
  'ceil_step',
  'min_line_price',
  'priority',
  'active',
  'show_in_menu',
  'applies_to_group',
];

const cardBg = '#ffffff';
const cardBorder = '1px solid #e2e8f0';
const cardRadius = '12px';
const cardShadow = '0 1px 3px rgba(0,0,0,0.06)';
const pageBg = '#f8fafc';
const textPrimary = '#0f172a';
const textSecondary = '#64748b';

function isImportFile(f) {
  if (!f || !f.name) return false;
  const lower = f.name.toLowerCase();
  if (lower.endsWith('.xlsx') || lower.endsWith('.xls') || lower.endsWith('.csv')) return true;
  const mime = f.type || '';
  return (
    mime === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
    mime === 'application/vnd.ms-excel' ||
    mime === 'text/csv'
  );
}

function triggerBlobDownload(blob, disposition, fallbackFilename) {
  let filename = fallbackFilename;
  if (disposition && typeof disposition === 'string') {
    const match = disposition.match(/filename="?([^";\n]+)"?/);
    if (match) filename = match[1].trim();
  }
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  window.URL.revokeObjectURL(url);
  document.body.removeChild(link);
}

function getPreviewColumns(preview) {
  if (!preview || preview.length === 0) return [];
  const keySet = new Set();
  preview.forEach((row) => Object.keys(row).forEach((k) => keySet.add(k)));
  keySet.delete('row_index');
  return [...keySet].sort((a, b) => {
    const ai = PREFERRED_PREVIEW_KEYS.indexOf(a);
    const bi = PREFERRED_PREVIEW_KEYS.indexOf(b);
    if (ai !== -1 && bi !== -1) return ai - bi;
    if (ai !== -1) return -1;
    if (bi !== -1) return 1;
    return String(a).localeCompare(String(b));
  });
}

function previewColumnWidth(key) {
  const widths = {
    code: { minWidth: 140, width: 180 },
    name: { minWidth: 160, width: 200 },
    name_ar: { minWidth: 140, width: 180 },
    description: { minWidth: 160, width: 220 },
    description_ar: { minWidth: 160, width: 220 },
    plan_code: { minWidth: 120, width: 140 },
    scope: { minWidth: 72, width: 88 },
    strategy: { minWidth: 140, width: 168 },
    tariff_code: { minWidth: 80, width: 96 },
    category_code: { minWidth: 100, width: 120 },
    customer: { minWidth: 88, width: 100 },
    customer_group: { minWidth: 120, width: 140 },
    shipment_type: { minWidth: 100, width: 120 },
    shipment_unit_id: { minWidth: 100, width: 120 },
    destination_city: { minWidth: 120, width: 140 },
    applies_to_group: { minWidth: 72, width: 88 },
    cbm_from: { minWidth: 72, width: 88 },
    cbm_to: { minWidth: 72, width: 88 },
    weight_from: { minWidth: 88, width: 100 },
    weight_to: { minWidth: 88, width: 100 },
    effective_from: { minWidth: 140, width: 168 },
    effective_to: { minWidth: 140, width: 168 },
    unit_price: { minWidth: 88, width: 100 },
    total_price: { minWidth: 88, width: 100 },
    ceil_step: { minWidth: 80, width: 96 },
    percent: { minWidth: 72, width: 88 },
    min_line_price: { minWidth: 100, width: 112 },
    threshold_cbm: { minWidth: 100, width: 112 },
    priority: { minWidth: 72, width: 88 },
    active: { minWidth: 64, width: 72 },
    show_in_menu: { minWidth: 88, width: 100 },
    menu_label_ar: { minWidth: 140, width: 180 },
    menu_label_en: { minWidth: 140, width: 180 },
    menu_order: { minWidth: 72, width: 88 },
    notes: { minWidth: 160, width: 220 },
  };
  return widths[key] || { minWidth: 88, width: 100 };
}

function humanizeFieldKey(key) {
  return String(key).replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function previewColumnLabel(key, t) {
  return t(`importPricingRules.previewCol.${key}`, { defaultValue: humanizeFieldKey(key) });
}

function formatNumeric(value, fallback = '—') {
  if (value == null || value === '') return fallback;
  const n = Number(value);
  if (!Number.isFinite(n)) return String(value);
  if (Number.isInteger(n)) return String(n);
  return n.toFixed(2).replace(/\.?0+$/, '');
}

function formatPreviewCellValue(key, value, t) {
  if (value == null || value === '') return '—';
  if (
    key === 'active' ||
    key === 'show_in_menu' ||
    key === 'applies_to_group'
  ) {
    const b = value === true || value === 1 || value === '1' || String(value).toLowerCase() === 'true';
    return b ? t('actions.yes') : t('actions.no');
  }
  if (key === 'effective_from' || key === 'effective_to') {
    const s = String(value);
    return s.length >= 16 ? s.slice(0, 16).replace('T', ' ') : s;
  }
  if (
    key === 'unit_price' ||
    key === 'total_price' ||
    key === 'min_line_price' ||
    key === 'ceil_step' ||
    key === 'percent' ||
    key === 'cbm_from' ||
    key === 'cbm_to' ||
    key === 'weight_from' ||
    key === 'weight_to' ||
    key === 'threshold_cbm'
  ) {
    return formatNumeric(value);
  }
  return String(value);
}

function isLongTextColumn(key) {
  return (
    key === 'name' ||
    key === 'name_ar' ||
    key === 'description' ||
    key === 'description_ar' ||
    key === 'menu_label_ar' ||
    key === 'menu_label_en' ||
    key === 'notes' ||
    key === 'customer_group' ||
    key === 'destination_city'
  );
}

export default function ImportPricingRulesScreen() {
  const { t } = useTranslation('ui');
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const canImport = hasPermission(PERMISSIONS.PRICING_RULES_IMPORT);

  const [file, setFile] = useState(null);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [templateLoading, setTemplateLoading] = useState(false);
  const [templateError, setTemplateError] = useState(null);
  const [sampleLoading, setSampleLoading] = useState(false);
  const [sampleError, setSampleError] = useState(null);
  const [inputKey, setInputKey] = useState(Date.now());
  const [dragActive, setDragActive] = useState(false);
  const dragDepthRef = useRef(0);
  const fileInputRef = useRef(null);

  const [preview, setPreview] = useState([]);
  const [globalErrors, setGlobalErrors] = useState([]);
  const [rowErrors, setRowErrors] = useState({});
  const [step, setStep] = useState('upload');
  const [selectedRowIndices, setSelectedRowIndices] = useState(new Set());
  const [confirmResult, setConfirmResult] = useState(null);

  const previewColumns = useMemo(() => getPreviewColumns(preview), [preview]);
  const rowErrorEntries = useMemo(
    () =>
      Object.entries(rowErrors).map(([rowNum, message]) => ({
        rowNum: Number(rowNum),
        message,
      })),
    [rowErrors]
  );

  const applyFile = (nextFile) => {
    if (!nextFile || !isImportFile(nextFile)) return;
    setFile(nextFile);
    setConfirmResult(null);
    setStep('upload');
  };

  const handleFileChange = (event) => {
    const next = event.target.files && event.target.files[0];
    applyFile(next);
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragDepthRef.current += 1;
    if (e.dataTransfer.types && e.dataTransfer.types.includes('Files')) {
      setDragActive(true);
    }
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragDepthRef.current = Math.max(0, dragDepthRef.current - 1);
    if (dragDepthRef.current === 0) setDragActive(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer) e.dataTransfer.dropEffect = 'copy';
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragDepthRef.current = 0;
    setDragActive(false);
    const dropped = e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files[0];
    if (dropped && isImportFile(dropped)) {
      applyFile(dropped);
    }
  };

  const handleDownloadTemplate = async () => {
    setTemplateError(null);
    setTemplateLoading(true);
    try {
      const response = await getPricingRulesImportTemplate();
      setTemplateLoading(false);
      if (response.status !== 200 || !response.data) {
        setTemplateError(t('importPricingRules.errorTemplateDownload'));
        return;
      }
      triggerBlobDownload(
        response.data,
        response.headers && response.headers['content-disposition'],
        'pricing_rules_import_template.xlsx'
      );
    } catch (err) {
      setTemplateLoading(false);
      const msg =
        err.response &&
        err.response.data &&
        (typeof err.response.data === 'string'
          ? err.response.data
          : err.response.data.detail || err.response.data.error);
      setTemplateError(msg || t('importPricingRules.errorTemplateDownload'));
    }
  };

  const handleDownloadSample = async () => {
    setSampleError(null);
    setSampleLoading(true);
    try {
      const response = await getPricingRulesImportSample();
      setSampleLoading(false);
      if (response.status !== 200 || !response.data) {
        setSampleError(t('importPricingRules.errorSampleDownload'));
        return;
      }
      triggerBlobDownload(
        response.data,
        response.headers && response.headers['content-disposition'],
        'pricing_rules_import_sample_5_rows.xlsx'
      );
    } catch (err) {
      setSampleLoading(false);
      const msg =
        err.response &&
        err.response.data &&
        (typeof err.response.data === 'string'
          ? err.response.data
          : err.response.data.detail || err.response.data.error);
      setSampleError(msg || t('importPricingRules.errorSampleDownload'));
    }
  };

  const handleUploadPreview = async (event) => {
    event.preventDefault();
    if (!file) return;
    if (!canImport) {
      setGlobalErrors([t('common.permissionDenied', { defaultValue: 'Permission denied.' })]);
      return;
    }
    setUploadLoading(true);
    setGlobalErrors([]);
    setRowErrors({});
    setPreview([]);
    setConfirmResult(null);
    try {
      const response = await postPricingRulesImportPreview(file);
      setUploadLoading(false);
      if (response.status === 200) {
        const data = response.data || {};
        setGlobalErrors(Array.isArray(data.errors) ? data.errors : []);
        setRowErrors(
          typeof data.row_errors === 'object' && data.row_errors !== null ? data.row_errors : {}
        );
        const prev = Array.isArray(data.preview) ? data.preview : [];
        setPreview(prev);
        setSelectedRowIndices(new Set(prev.map((p) => p.row_index)));
        setStep('preview');
      } else {
        setGlobalErrors([
          (response.data && (response.data.error || response.data.detail)) ||
            t('importPricingRules.uploadFailed'),
        ]);
      }
    } catch (err) {
      setUploadLoading(false);
      const msg =
        err.response && err.response.data && (err.response.data.error || err.response.data.detail);
      setGlobalErrors([msg || t('importPricingRules.uploadErrorGeneric')]);
    }
  };

  const handleConfirmImport = async () => {
    const toSend = preview.filter((p) => selectedRowIndices.has(p.row_index));
    if (toSend.length === 0) return;
    if (!canImport) {
      setGlobalErrors([t('common.permissionDenied', { defaultValue: 'Permission denied.' })]);
      return;
    }
    setConfirmLoading(true);
    setConfirmResult(null);
    try {
      const response = await postPricingRulesImportConfirm(toSend);
      setConfirmLoading(false);
      if (response.status === 200) {
        const data = response.data || {};
        setConfirmResult({
          detail:
            data.detail ||
            t('importPricingRules.importedCountDetail', {
              count: data.created_count != null ? data.created_count : 0,
            }),
          created_count: data.created_count,
          errors: Array.isArray(data.errors) ? data.errors : [],
        });
        setStep('done');
      } else {
        setConfirmResult({
          detail:
            (response.data && (response.data.error || response.data.detail)) ||
            t('importPricingRules.importFailed'),
          created_count: 0,
          errors: [],
        });
      }
    } catch (err) {
      setConfirmLoading(false);
      const msg =
        err.response && err.response.data && (err.response.data.error || err.response.data.detail);
      setConfirmResult({
        detail: msg || t('importPricingRules.confirmImportError'),
        created_count: 0,
        errors: [],
      });
    }
  };

  const allSelected =
    preview.length > 0 && preview.every((p) => selectedRowIndices.has(p.row_index));
  const someSelected = selectedRowIndices.size > 0;
  const toggleSelectAll = () => {
    if (allSelected) {
      setSelectedRowIndices(new Set());
    } else {
      setSelectedRowIndices(new Set(preview.map((p) => p.row_index)));
    }
  };
  const toggleSelectRow = (rowIndex) => {
    setSelectedRowIndices((prev) => {
      const next = new Set(prev);
      if (next.has(rowIndex)) next.delete(rowIndex);
      else next.add(rowIndex);
      return next;
    });
  };

  const startOver = () => {
    dragDepthRef.current = 0;
    setDragActive(false);
    setFile(null);
    setInputKey(Date.now());
    setPreview([]);
    setGlobalErrors([]);
    setRowErrors({});
    setSelectedRowIndices(new Set());
    setConfirmResult(null);
    setStep('upload');
  };

  const hasBlockingFileErrors = globalErrors.length > 0 && step === 'upload';

  const structureRows = useMemo(
    () => [
      { id: 0, fieldName: 'code', valueRange: t('importPricingRules.docCode'), exampleValue: 'unit_42_meter', required: true },
      { id: 1, fieldName: 'name', valueRange: t('importPricingRules.docName'), exampleValue: 'Unit 42 meter rate', required: true },
      { id: 2, fieldName: 'name_ar', valueRange: t('importPricingRules.docNameAr'), exampleValue: 'سعر متر الوحدة', required: false },
      { id: 3, fieldName: 'plan_code', valueRange: t('importPricingRules.docPlanCode'), exampleValue: 'sea_freight', required: false },
      { id: 4, fieldName: 'description', valueRange: t('importPricingRules.docDescription'), exampleValue: 'Negotiated load rate', required: false },
      { id: 5, fieldName: 'description_ar', valueRange: t('importPricingRules.docDescriptionAr'), exampleValue: 'سعر متفق عليه', required: false },
      { id: 6, fieldName: 'scope', valueRange: t('importPricingRules.docScope'), exampleValue: 'base', required: false },
      { id: 7, fieldName: 'strategy', valueRange: t('importPricingRules.docStrategy'), exampleValue: 'meter_per_line', required: false },
      { id: 8, fieldName: 'tariff_code', valueRange: t('importPricingRules.docTariffCode'), exampleValue: 'T1', required: false },
      { id: 9, fieldName: 'category_code', valueRange: t('importPricingRules.docCategoryCode'), exampleValue: 'normal', required: false },
      { id: 10, fieldName: 'customer', valueRange: t('importPricingRules.docCustomer'), exampleValue: '12', required: false },
      { id: 11, fieldName: 'customer_group', valueRange: t('importPricingRules.docCustomerGroup'), exampleValue: 'vip', required: false },
      { id: 12, fieldName: 'shipment_type', valueRange: t('importPricingRules.docShipmentType'), exampleValue: '1', required: false },
      { id: 13, fieldName: 'shipment_unit_id', valueRange: t('importPricingRules.docShipmentUnitId'), exampleValue: '42', required: false },
      { id: 14, fieldName: 'destination_city', valueRange: t('importPricingRules.docDestinationCity'), exampleValue: 'Muscat', required: false },
      { id: 15, fieldName: 'applies_to_group', valueRange: t('importPricingRules.docAppliesToGroup'), exampleValue: '0', required: false },
      { id: 16, fieldName: 'cbm_from', valueRange: t('importPricingRules.docCbmFrom'), exampleValue: '0.950', required: false },
      { id: 17, fieldName: 'cbm_to', valueRange: t('importPricingRules.docCbmTo'), exampleValue: '', required: false },
      { id: 18, fieldName: 'weight_from', valueRange: t('importPricingRules.docWeightFrom'), exampleValue: '', required: false },
      { id: 19, fieldName: 'weight_to', valueRange: t('importPricingRules.docWeightTo'), exampleValue: '', required: false },
      { id: 20, fieldName: 'effective_from', valueRange: t('importPricingRules.docEffectiveFrom'), exampleValue: '', required: false },
      { id: 21, fieldName: 'effective_to', valueRange: t('importPricingRules.docEffectiveTo'), exampleValue: '', required: false },
      { id: 22, fieldName: 'unit_price', valueRange: t('importPricingRules.docUnitPrice'), exampleValue: '28.0000', required: false },
      { id: 23, fieldName: 'total_price', valueRange: t('importPricingRules.docTotalPrice'), exampleValue: '120.0000', required: false },
      { id: 24, fieldName: 'ceil_step', valueRange: t('importPricingRules.docCeilStep'), exampleValue: '0.5', required: false },
      { id: 25, fieldName: 'percent', valueRange: t('importPricingRules.docPercent'), exampleValue: '5', required: false },
      { id: 26, fieldName: 'min_line_price', valueRange: t('importPricingRules.docMinLinePrice'), exampleValue: '3.000', required: false },
      { id: 27, fieldName: 'threshold_cbm', valueRange: t('importPricingRules.docThresholdCbm'), exampleValue: '', required: false },
      { id: 28, fieldName: 'priority', valueRange: t('importPricingRules.docPriority'), exampleValue: '50', required: false },
      { id: 29, fieldName: 'active', valueRange: t('importPricingRules.docActive'), exampleValue: '0', required: false },
      { id: 30, fieldName: 'show_in_menu', valueRange: t('importPricingRules.docShowInMenu'), exampleValue: '0', required: false },
      { id: 31, fieldName: 'menu_label_en', valueRange: t('importPricingRules.docMenuLabelEn'), exampleValue: '', required: false },
      { id: 32, fieldName: 'menu_label_ar', valueRange: t('importPricingRules.docMenuLabelAr'), exampleValue: '', required: false },
      { id: 33, fieldName: 'menu_order', valueRange: t('importPricingRules.docMenuOrder'), exampleValue: '0', required: false },
      { id: 34, fieldName: 'notes', valueRange: t('importPricingRules.docNotes'), exampleValue: 'Review before enabling', required: false },
    ],
    [t]
  );

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box sx={{ minHeight: '100vh', bgcolor: pageBg }}>
        <Box component="main" sx={{ overflow: 'auto', pt: { xs: 2, sm: 3 }, pb: { xs: 2, sm: 3 }, px: 3 }}>
          <Box mb={4}>
            <Breadcrumbs separator={<KeyboardArrowRight />} aria-label="breadcrumbs">
              <Link color="neutral" href="/PricingRules">
                {t('importPricingRules.breadcrumb1')}
              </Link>
              <Link color="neutral" href="/PricingRules/Import">
                {t('importPricingRules.breadcrumb2')}
              </Link>
            </Breadcrumbs>
            <Typography
              textAlign="start"
              level="h1"
              sx={{ mt: 2, fontSize: '2rem', fontWeight: 600, color: textPrimary }}
            >
              {t('importPricingRules.pageTitle')}
            </Typography>
          </Box>

          <PermissionGate permission={PERMISSIONS.PRICING_RULES_IMPORT}>
            <Card
              variant="plain"
              sx={{
                bgcolor: cardBg,
                boxShadow: cardShadow,
                borderRadius: cardRadius,
                border: cardBorder,
                mb: 3,
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Typography level="title-md" sx={{ mb: 2, color: textPrimary, fontWeight: 600 }}>
                  {t('importPricingRules.step1Title')}
                </Typography>
                <Box sx={{ mb: 2, display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                  <Button
                    variant="soft"
                    size="md"
                    startDecorator={<DownloadIcon />}
                    onClick={handleDownloadTemplate}
                    loading={templateLoading}
                    disabled={templateLoading || sampleLoading}
                  >
                    {t('importPricingRules.downloadTemplate')}
                  </Button>
                  <Button
                    variant="outlined"
                    size="md"
                    startDecorator={<DownloadIcon />}
                    onClick={handleDownloadSample}
                    loading={sampleLoading}
                    disabled={templateLoading || sampleLoading}
                  >
                    {t('importPricingRules.downloadSample')}
                  </Button>
                </Box>
                <Typography level="body2" sx={{ color: textSecondary, mb: 2 }}>
                  {t('importPricingRules.introParagraph')}
                </Typography>
                {templateError && (
                  <Alert color="danger" variant="soft" size="sm" sx={{ mb: 2 }}>
                    {templateError}
                  </Alert>
                )}
                {sampleError && (
                  <Alert color="danger" variant="soft" size="sm" sx={{ mb: 2 }}>
                    {sampleError}
                  </Alert>
                )}
                <Box
                  onDragEnter={handleDragEnter}
                  onDragLeave={handleDragLeave}
                  onDragOver={handleDragOver}
                  onDrop={handleDrop}
                  role="region"
                  aria-label={t('importPricingRules.dropZoneAria')}
                  sx={{
                    border: '2px dashed',
                    borderColor: dragActive ? '#3b82f6' : '#cbd5e1',
                    borderRadius: cardRadius,
                    bgcolor: dragActive ? 'rgba(59, 130, 246, 0.06)' : 'rgba(248, 250, 252, 0.9)',
                    p: 2.5,
                    mb: 2,
                  }}
                >
                  <Box sx={{ textAlign: 'center', mb: 2 }}>
                    <CloudUploadIcon
                      sx={{ fontSize: 40, color: dragActive ? '#3b82f6' : textSecondary }}
                      aria-hidden
                    />
                    <Typography level="body2" sx={{ color: textSecondary, mt: 1 }}>
                      {t('importPricingRules.dropHint')}
                    </Typography>
                    {file && (
                      <Typography level="body2" sx={{ color: textPrimary, fontWeight: 600, mt: 1 }}>
                        {t('importPricingRules.selectedFile', { name: file.name })}
                      </Typography>
                    )}
                  </Box>
                  <Grid container spacing={2} alignItems="center">
                    <Grid xs={12} sm={6}>
                      <input
                        ref={fileInputRef}
                        key={inputKey}
                        type="file"
                        accept=".xlsx,.xls,.csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel,text/csv"
                        onChange={handleFileChange}
                        style={{ display: 'none' }}
                      />
                      <Button
                        type="button"
                        variant="outlined"
                        color="neutral"
                        startDecorator={<UploadFileIcon />}
                        onClick={() => fileInputRef.current?.click()}
                      >
                        {t('importPricingRules.fileInputPlaceholder')}
                      </Button>
                      <Typography level="body2" sx={{ mt: 1, color: file ? textPrimary : textSecondary }}>
                        {file ? file.name : t('importPricingRules.noFileSelected')}
                      </Typography>
                    </Grid>
                    <Grid xs={12} sm={6}>
                      <Button
                        type="button"
                        onClick={handleUploadPreview}
                        loading={uploadLoading}
                        disabled={!file || uploadLoading}
                        startDecorator={<CloudUploadIcon />}
                        color="primary"
                        variant="solid"
                      >
                        {t('importPricingRules.validatePreview')}
                      </Button>
                    </Grid>
                  </Grid>
                </Box>
              </CardContent>
            </Card>

            {globalErrors.length > 0 && (
              <Alert
                variant="soft"
                color="danger"
                startDecorator={<ErrorIcon />}
                sx={{ mb: 3 }}
              >
                {globalErrors.map((msg, i) => (
                  <Typography key={i} level="body2">
                    {msg}
                  </Typography>
                ))}
              </Alert>
            )}

            {step === 'preview' && rowErrorEntries.length > 0 && (
              <Alert variant="soft" color="danger" startDecorator={<ErrorIcon />} sx={{ mb: 3 }}>
                <Typography level="title-sm" sx={{ mb: 1 }}>
                  {t('importPricingRules.rowsWithErrorsTitle')}
                </Typography>
                <Box component="ul" sx={{ m: 0, pl: 2 }}>
                  {rowErrorEntries.map(({ rowNum, message }) => (
                    <li key={rowNum}>
                      <Typography level="body2">
                        {t('importPricingRules.rowErrorLine', { n: rowNum, msg: message })}
                      </Typography>
                    </li>
                  ))}
                </Box>
              </Alert>
            )}

            {step === 'preview' && preview.length > 0 && (
              <Card
                variant="plain"
                sx={{
                  bgcolor: cardBg,
                  boxShadow: cardShadow,
                  borderRadius: cardRadius,
                  border: cardBorder,
                  mb: 3,
                }}
              >
                <CardContent sx={{ p: 2 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      flexWrap: 'wrap',
                      gap: 2,
                      mb: 2,
                    }}
                  >
                    <Typography level="title-md" sx={{ color: textPrimary, fontWeight: 600 }}>
                      {t('importPricingRules.step2Title')}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      <Button
                        variant="soft"
                        size="sm"
                        startDecorator={<DoneAllIcon />}
                        onClick={toggleSelectAll}
                      >
                        {allSelected
                          ? t('importPricingRules.deselectAll')
                          : t('importPricingRules.selectAll')}
                      </Button>
                      <Button
                        variant="solid"
                        color="primary"
                        size="sm"
                        startDecorator={<PlaylistAddCheckIcon />}
                        onClick={handleConfirmImport}
                        loading={confirmLoading}
                        disabled={!someSelected || confirmLoading || hasBlockingFileErrors}
                      >
                        {t('importPricingRules.importSelected', { count: selectedRowIndices.size })}
                      </Button>
                    </Box>
                  </Box>
                  <Typography level="body2" sx={{ color: textSecondary, mb: 2 }}>
                    {t('importPricingRules.previewHint', { count: preview.length })}
                  </Typography>
                  <Box
                    sx={{
                      maxHeight: '65vh',
                      overflow: 'auto',
                      borderRadius: cardRadius,
                      border: cardBorder,
                      WebkitOverflowScrolling: 'touch',
                    }}
                  >
                    <Sheet
                      variant="outlined"
                      sx={{
                        borderRadius: cardRadius,
                        border: 'none',
                        boxShadow: cardShadow,
                        display: 'inline-block',
                        minWidth: '100%',
                        '& thead th': {
                          fontWeight: 600,
                          color: textSecondary,
                          fontSize: '0.8125rem',
                          whiteSpace: 'nowrap',
                        },
                        '& tbody tr:hover': { bgcolor: 'rgba(0,0,0,0.04)' },
                        '& td': { verticalAlign: 'middle', fontSize: '0.875rem' },
                        '& thead th:first-of-type': {
                          position: 'sticky',
                          top: 0,
                          insetInlineStart: 0,
                          zIndex: 12,
                          minWidth: 48,
                          width: 48,
                          backgroundColor: cardBg,
                          backgroundClip: 'padding-box',
                          boxShadow: '4px 0 8px rgba(0,0,0,0.08)',
                          pointerEvents: 'auto',
                          isolation: 'isolate',
                        },
                        'html[dir="rtl"] & thead th:first-of-type': {
                          boxShadow: '-4px 0 8px rgba(0,0,0,0.08)',
                        },
                        '& tbody td:first-of-type': {
                          position: 'sticky',
                          insetInlineStart: 0,
                          zIndex: 11,
                          backgroundColor: cardBg,
                          backgroundClip: 'padding-box',
                          boxShadow: '4px 0 8px rgba(0,0,0,0.06)',
                          pointerEvents: 'auto',
                          isolation: 'isolate',
                        },
                        'html[dir="rtl"] & tbody td:first-of-type': {
                          boxShadow: '-4px 0 8px rgba(0,0,0,0.06)',
                        },
                        '& tbody tr:hover td:first-of-type': {
                          backgroundColor: 'rgba(0,0,0,0.04)',
                        },
                      }}
                    >
                      <Table
                        stickyHeader
                        hoverRow
                        size="md"
                        sx={{ minWidth: 800, borderCollapse: 'separate', borderSpacing: 0 }}
                      >
                        <thead>
                          <tr>
                            <th style={{ width: 48, minWidth: 48 }}>
                              <NativeCheckbox
                                checked={allSelected}
                                indeterminate={someSelected && !allSelected}
                                onChange={toggleSelectAll}
                                sx={{ verticalAlign: 'middle' }}
                              />
                            </th>
                            <th style={{ width: 56, minWidth: 56 }}>{t('importPricingRules.thRow')}</th>
                            {previewColumns.map((key) => {
                              const { minWidth, width } = previewColumnWidth(key);
                              return (
                                <th key={key} style={{ minWidth, width }}>
                                  {previewColumnLabel(key, t)}
                                </th>
                              );
                            })}
                          </tr>
                        </thead>
                        <tbody>
                          {preview.map((row) => (
                            <tr key={`row-${row.row_index}`}>
                              <td>
                                <NativeCheckbox
                                  checked={selectedRowIndices.has(row.row_index)}
                                  onChange={() => toggleSelectRow(row.row_index)}
                                  sx={{ verticalAlign: 'middle' }}
                                />
                              </td>
                              <td>
                                <Typography level="body2" sx={{ fontWeight: 600, color: textPrimary }}>
                                  {row.row_index}
                                </Typography>
                              </td>
                              {previewColumns.map((key) => {
                                const value = row[key];
                                const display = formatPreviewCellValue(key, value, t);
                                const { minWidth, width } = previewColumnWidth(key);
                                return (
                                  <td key={key} style={{ minWidth, width }}>
                                    {key === 'code' && value != null && value !== '' ? (
                                      <Chip
                                        size="sm"
                                        variant="soft"
                                        color="primary"
                                        sx={{ fontFamily: 'monospace', maxWidth: width - 8 }}
                                      >
                                        <Typography level="body2" noWrap title={display}>
                                          {display}
                                        </Typography>
                                      </Chip>
                                    ) : key === 'scope' || key === 'strategy' ? (
                                      <Chip size="sm" variant="soft" color="neutral" sx={{ maxWidth: width - 8 }}>
                                        <Typography level="body2" noWrap title={display}>
                                          {display}
                                        </Typography>
                                      </Chip>
                                    ) : isLongTextColumn(key) ? (
                                      <Typography
                                        level="body2"
                                        sx={{ maxWidth: width - 8 }}
                                        noWrap
                                        title={display}
                                      >
                                        {display}
                                      </Typography>
                                    ) : (
                                      display
                                    )}
                                  </td>
                                );
                              })}
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </Sheet>
                  </Box>
                </CardContent>
              </Card>
            )}

            {step === 'preview' && preview.length === 0 && !hasBlockingFileErrors && (
              <Alert variant="soft" color="warning" sx={{ mb: 3 }}>
                {t('importPricingRules.noValidRows')}
              </Alert>
            )}

            {step === 'done' && confirmResult && (
              <Card
                variant="plain"
                sx={{
                  bgcolor: cardBg,
                  boxShadow: cardShadow,
                  borderRadius: cardRadius,
                  border: cardBorder,
                  mb: 3,
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Alert
                    variant="soft"
                    color={confirmResult.created_count > 0 ? 'success' : 'warning'}
                    startDecorator={<CheckCircleIcon />}
                    sx={{ mb: 2 }}
                  >
                    {confirmResult.detail}
                  </Alert>
                  {confirmResult.errors.length > 0 && (
                    <Alert variant="soft" color="danger" startDecorator={<ErrorIcon />} sx={{ mb: 2 }}>
                      <Box component="ul" sx={{ m: 0, pl: 2 }}>
                        {confirmResult.errors.map((item, i) => (
                          <li key={i}>
                            <Typography level="body2">
                              {t('importPricingRules.confirmErrorRow', {
                                n: item.row_index,
                                msg: item.error,
                              })}
                            </Typography>
                          </li>
                        ))}
                      </Box>
                    </Alert>
                  )}
                  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                    <Button variant="solid" color="primary" onClick={() => navigate('/PricingRules')}>
                      {t('importPricingRules.backToList')}
                    </Button>
                    <Button variant="outlined" color="neutral" onClick={startOver}>
                      {t('importPricingRules.importAnotherFile')}
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            )}
          </PermissionGate>

          <Card
            variant="plain"
            sx={{
              p: 3,
              borderRadius: cardRadius,
              backgroundColor: cardBg,
              border: cardBorder,
              boxShadow: cardShadow,
              mb: 3,
            }}
          >
            <Typography level="h4" startDecorator={<InfoIcon />} sx={{ mb: 1, color: textPrimary }}>
              {t('importPricingRules.excelStructureTitle')}
            </Typography>
            <Typography level="body1" sx={{ mb: 2, color: textSecondary }}>
              {t('importPricingRules.excelStructureIntro')}
            </Typography>
            <Chip size="sm" variant="soft" color="primary" sx={{ mb: 2 }}>
              {t('importPricingRules.rulesSheetTab')}
            </Chip>
            <DataGrid
              rows={structureRows}
              columns={[
                {
                  field: 'fieldName',
                  headerName: t('importPricingRules.dgFieldName'),
                  flex: 2,
                  minWidth: 180,
                  headerClassName: 'super-app-theme--header',
                },
                {
                  field: 'valueRange',
                  headerName: t('importPricingRules.dgValueRange'),
                  flex: 3,
                  minWidth: 200,
                  headerClassName: 'super-app-theme--header',
                },
                {
                  field: 'exampleValue',
                  headerName: t('importPricingRules.dgExampleValue'),
                  flex: 2,
                  minWidth: 150,
                  headerClassName: 'super-app-theme--header',
                },
                {
                  field: 'required',
                  headerName: t('importPricingRules.dgRequired'),
                  flex: 1,
                  minWidth: 100,
                  headerClassName: 'super-app-theme--header',
                  renderCell: (params) =>
                    params.value ? (
                      <CheckCircleIcon color="success" />
                    ) : (
                      <CancelIcon color="error" />
                    ),
                },
              ]}
              hideFooter
              disableColumnMenu
              disableRowSelectionOnClick
              getRowHeight={() => 'auto'}
              sx={{
                '& .MuiDataGrid-cell': {
                  whiteSpace: 'normal',
                  wordWrap: 'break-word',
                  lineHeight: 'normal',
                  padding: '12px 8px',
                },
                '& .super-app-theme--header': {
                  backgroundColor: 'primary.softBg',
                  fontWeight: 'bold',
                },
                '@media (max-width: 600px)': {
                  '& .MuiDataGrid-toolbarContainer': { flexDirection: 'column', gap: 1 },
                  '& .MuiDataGrid-footer': { flexDirection: 'column', gap: 1 },
                  '& .MuiDataGrid-cell': { minHeight: '60px', alignItems: 'center', padding: '8px' },
                },
              }}
            />
            <Alert
              variant="soft"
              color="neutral"
              size="sm"
              startDecorator={<InfoIcon />}
              sx={{ mt: 2, borderRadius: '10px' }}
            >
              <Typography level="body2">{t('importPricingRules.structureFooterHint')}</Typography>
            </Alert>
          </Card>
        </Box>
      </Box>
    </ThemeProvider>
  );
}
